import { useEffect, useMemo, useRef } from "react";

import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";
import { detectPixKey, formatPixInput, PIX_KEY_META, type PixKeyTypeEnum } from "@/shared/utils/pixKey";

import type { PixSendRecipient } from "../../utils/walletSend";
import {
  EMPTY_PIX_VALUES,
  PIX_KEY_TYPES,
  pixRecipientSchema,
  type PixRecipientValues,
} from "./sendRecipients.schema";

export interface PixRecipientFormProps {
  defaults: PixRecipientValues;
  /** Every edit: the raw values, and the recipient once they are complete. */
  onChange: (values: PixRecipientValues, recipient: PixSendRecipient | null) => void;
}

const TYPE_LABEL = {
  CPF: null,
  CNPJ: null,
  PHONE: "step3_pix_type_phone",
  EMAIL: "step3_pix_type_email",
  EVP: "step3_pix_type_evp",
} as const satisfies Record<PixKeyTypeEnum, string | null>;

/**
 * A PIX key and who it belongs to. The type is read off what is pasted (`detectPixKey`) until the person
 * picks one by hand; from then on their choice stands, since eleven digits can be a CPF or a mobile.
 */
export function PixRecipientForm({ defaults, onChange }: PixRecipientFormProps) {
  const { t } = useTranslations("dispersions");
  const { t: tGlobal } = useTranslations("global");
  const schema = useMemo(
    () =>
      pixRecipientSchema({
        key: {
          CPF: tGlobal("pix_key_err_cpf"),
          CNPJ: tGlobal("pix_key_err_cnpj"),
          PHONE: tGlobal("pix_key_err_phone"),
          EMAIL: tGlobal("pix_key_err_email"),
          EVP: tGlobal("pix_key_err_evp"),
        },
        keyCheck: t("send_pix_key_check_error"),
        name: t("error_recipient_name_required"),
      }),
    [t, tGlobal],
  );
  const form = useZodForm(schema, defaults);
  // A type chosen by hand is never overridden by a guess.
  const typeChosen = useRef(defaults.keyValue !== "");
  const keyType = form.watch("keyType");
  const meta = PIX_KEY_META[keyType];

  useEffect(() => {
    const report = (values: Partial<PixRecipientValues>) => {
      const full = { ...EMPTY_PIX_VALUES, ...values } as PixRecipientValues;
      const parsed = schema.safeParse(full);
      onChange(full, parsed.success ? { rail: "PIX", ...parsed.data } : null);
    };
    report(form.getValues());
    const subscription = form.watch((values) => report(values as Partial<PixRecipientValues>));
    return () => subscription.unsubscribe();
  }, [form, schema, onChange]);

  // Picking a type by hand (a "change" event; a guess is a `setValue`, which carries none) locks it, and the
  // key already typed is re-masked for it.
  useEffect(() => {
    const subscription = form.watch((values, { name, type }) => {
      if (name !== "keyType" || type !== "change" || !values.keyType) return;
      typeChosen.current = true;
      form.setValue("keyValue", formatPixInput(values.keyType, form.getValues("keyValue")), {
        shouldValidate: form.getFieldState("keyValue").isTouched,
      });
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const transformKey = (raw: string): string => {
    const current = form.getValues("keyType");
    const detected = typeChosen.current ? null : detectPixKey(raw);
    if (detected && detected.type !== current) {
      form.setValue("keyType", detected.type, { shouldValidate: false });
    }
    return formatPixInput(detected?.type ?? current, detected?.value ?? raw);
  };

  return (
    <ColocaForm form={form} onSubmit={() => undefined} className="space-y-4">
      <ColocaInputField
        name="beneficiaryName"
        label={t("send_name_label")}
        placeholder={t("send_name_placeholder")}
        maxLength={120}
        autoComplete="off"
        required
      />
      <div className="grid items-start gap-4 sm:grid-cols-[minmax(0,10rem)_minmax(0,1fr)]">
        <ColocaSelectField
          name="keyType"
          label={t("step2_pix_type_label")}
          items={PIX_KEY_TYPES.map((type) => ({
            value: type,
            label: TYPE_LABEL[type] ? t(TYPE_LABEL[type]) : type,
          }))}
          required
        />
        <ColocaInputField
          name="keyValue"
          label={t("step3_pix_key_label")}
          placeholder={tGlobal(meta.ph)}
          description={t("send_pix_key_hint")}
          inputMode={keyType === "EMAIL" || keyType === "EVP" ? "text" : "numeric"}
          autoComplete="off"
          transform={transformKey}
          required
        />
      </div>
    </ColocaForm>
  );
}
